<template>
  <div
    class="html-preview-slider"
    @click.self="$emit('close')"
  >
    <div class="html-preview-slider__panel">
      <!-- Header -->
      <div class="html-preview-slider__header">
        <div class="html-preview-slider__title">
          <i class="fa-light fa-eye" />
          <span>{{ isFlowPreview ? t('import.htmlPreview.titleFlow') : t('import.htmlPreview.titleTemplate') }}</span>
          <span
            v-if="totalCount > 1"
            class="html-preview-slider__count-badge"
          >
            {{ currentIndex + 1 }} / {{ totalCount }}
          </span>
        </div>

        <div class="html-preview-slider__controls">
          <!-- Desktop / Mobile toggle (hidden when HTML already contains a phone frame or hideMediaToggle is set) -->
          <div
            v-if="!hasPhoneWrapper && !hideMediaToggle"
            class="html-preview-slider__mode-toggle"
          >
            <button
              class="mode-btn"
              :class="{ 'mode-btn--active': viewMode === 'desktop' }"
              :title="t('import.htmlPreview.desktop')"
              @click="viewMode = 'desktop'"
            >
              <i class="fa-light fa-desktop" />
            </button>
            <button
              class="mode-btn"
              :class="{ 'mode-btn--active': viewMode === 'mobile' }"
              :title="t('import.htmlPreview.mobile')"
              @click="viewMode = 'mobile'"
            >
              <i class="fa-light fa-mobile-screen" />
            </button>
          </div>

          <!-- Close -->
          <button
            class="html-preview-slider__close"
            @click="$emit('close')"
          >
            <i class="fa-light fa-xmark" />
          </button>
        </div>
      </div>

      <!-- Slide tabs (when multiple previews) -->
      <div
        v-if="totalCount > 1"
        class="html-preview-slider__tabs"
      >
        <button
          v-for="idx in totalCount"
          :key="idx - 1"
          class="slide-tab"
          :class="{ 'slide-tab--active': currentIndex === idx - 1 }"
          @click="goTo(idx - 1)"
        >
          <i
            :class="idx === 1 ? 'fa-light fa-star' : 'fa-light fa-arrow-right-arrow-left'"
            class="slide-tab__icon"
          />
          {{ getTabLabel(idx - 1) }}
        </button>
      </div>

      <!-- Preview iframe area -->
      <div
        ref="viewportRef"
        class="html-preview-slider__viewport"
        :class="`html-preview-slider__viewport--${viewMode}`"
      >
        <!-- Loading state -->
        <div
          v-if="loading"
          class="html-preview-slider__loading"
        >
          <i class="fa-light fa-spinner-third fa-spin" />
          <span>{{ t('import.htmlPreview.loading') }}</span>
        </div>

        <!-- Error state -->
        <div
          v-else-if="error"
          class="html-preview-slider__error"
        >
          <i class="fa-light fa-triangle-exclamation" />
          <span>{{ error }}</span>
        </div>

        <!-- Flow SVG preview with zoom/pan -->
        <div
          v-else-if="currentHtml && isFlowPreview"
          ref="flowWrapperRef"
          class="html-preview-slider__frame-wrapper"
          @wheel.prevent="onFlowWheel"
          @mousedown="onFlowMouseDown"
          @mousemove="onFlowMouseMove"
          @mouseup="onFlowMouseUp"
          @mouseleave="onFlowMouseUp"
        >
          <div
            class="html-preview-slider__flow-canvas"
            :style="flowCanvasStyle"
            v-html="flowSvgContent"
          />
          <div class="html-preview-slider__flow-controls">
            <button
              :title="t('import.htmlPreview.resetZoom')"
              class="flow-ctrl-btn"
              @click="resetFlowZoom"
            >
              <i class="fa-light fa-arrows-minimize" />
            </button>
            <button
              :title="t('import.htmlPreview.zoomOut')"
              class="flow-ctrl-btn"
              @click="flowZoomOut"
            >
              <i class="fa-light fa-minus" />
            </button>
            <button
              :title="t('import.htmlPreview.zoomIn')"
              class="flow-ctrl-btn"
              @click="flowZoomIn"
            >
              <i class="fa-light fa-plus" />
            </button>
          </div>
        </div>

        <!-- Iframe (templates) -->
        <div
          v-else-if="currentHtml"
          class="html-preview-slider__frame-wrapper"
          :class="[
            `html-preview-slider__frame-wrapper--${viewMode}`,
            { 'html-preview-slider__frame-wrapper--phone': hasPhoneWrapper },
          ]"
          :style="hasPhoneWrapper ? phoneFrameStyle : {}"
        >
          <iframe
            :key="currentIndex"
            class="html-preview-slider__iframe"
            sandbox="allow-same-origin"
            :srcdoc="currentHtml"
          />
        </div>
      </div>

      <!-- Navigation arrows (when multiple) -->
      <div
        v-if="totalCount > 1"
        class="html-preview-slider__nav"
      >
        <button
          class="nav-arrow"
          :disabled="currentIndex === 0 || loading"
          @click="goTo(currentIndex - 1)"
        >
          <i class="fa-light fa-chevron-left" />
        </button>
        <div class="nav-dots">
          <button
            v-for="idx in totalCount"
            :key="idx - 1"
            class="nav-dot"
            :class="{ 'nav-dot--active': currentIndex === idx - 1 }"
            @click="goTo(idx - 1)"
          />
        </div>
        <button
          class="nav-arrow"
          :disabled="currentIndex === totalCount - 1 || loading"
          @click="goTo(currentIndex + 1)"
        >
          <i class="fa-light fa-chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';

export default defineComponent({
  name: 'TemplateHtmlPreviewSlider',

  props: {
    sessionId: {
      type: String,
      default: '',
    },

    uid: {
      type: String,
      default: '',
    },

    previewCount: {
      type: Number,
      default: 0,
    },

    previews: {
      type: Array as () => { html: string; label: string }[],
      default: () => [],
    },

    hideMediaToggle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const currentIndex = ref(0);
    const viewMode = ref<'desktop' | 'mobile'>('desktop');
    const loading = ref(false);
    const error = ref('');
    const currentHtml = ref('');

    // Determine mode: direct previews vs fetch-based
    const isDirectMode = computed(() => props.previews && props.previews.length > 0);
    const totalCount = computed(() => (isDirectMode.value ? props.previews.length : props.previewCount));

    // Viewport measurement for phone scaling
    const viewportRef = ref<HTMLElement | null>(null);
    const viewportHeight = ref(0);
    const PHONE_NATIVE_HEIGHT = 820;
    let resizeObserver: ResizeObserver | null = null;

    // Detect if the HTML already contains a phone wrapper (sms/whatsapp/push)
    const hasPhoneWrapper = computed(() => currentHtml.value.includes('spm-phone-wrapper'));

    // Detect if the HTML is a flow SVG preview (not a template)
    const isFlowPreview = computed(() => currentHtml.value.includes('<svg xmlns='));

    // Extract SVG content from flow HTML for direct rendering
    const flowSvgContent = computed(() => {
      if (!isFlowPreview.value) return '';
      const match = currentHtml.value.match(/<svg[\s\S]*<\/svg>/);
      return match ? match[0] : '';
    });

    // Flow zoom/pan state
    const flowScale = ref(1);
    const flowTx = ref(0);
    const flowTy = ref(0);
    const flowDragging = ref(false);
    const flowDragStart = ref({ x: 0, y: 0, tx: 0, ty: 0 });

    const flowCanvasStyle = computed(() => ({
      transform: `translate(${flowTx.value}px, ${flowTy.value}px) scale(${flowScale.value})`,
      transformOrigin: '0 0',
      cursor: flowDragging.value ? 'grabbing' : 'grab',
    }));

    const resetFlowZoom = () => {
      flowFitView();
    };

    const flowZoomIn = () => {
      const ns = Math.min(flowScale.value + 0.2, 5);
      flowScale.value = ns;
    };

    const flowZoomOut = () => {
      const ns = Math.max(flowScale.value - 0.2, 0.1);
      flowScale.value = ns;
    };

    const onFlowWheel = (e: WheelEvent) => {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const ns = Math.min(Math.max(flowScale.value + delta, 0.1), 5);
      const ratio = ns / flowScale.value;
      flowTx.value = e.offsetX - (e.offsetX - flowTx.value) * ratio;
      flowTy.value = e.offsetY - (e.offsetY - flowTy.value) * ratio;
      flowScale.value = ns;
    };

    const onFlowMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      flowDragging.value = true;
      flowDragStart.value = {
        x: e.clientX, y: e.clientY, tx: flowTx.value, ty: flowTy.value,
      };
    };

    const onFlowMouseMove = (e: MouseEvent) => {
      if (!flowDragging.value) return;
      flowTx.value = flowDragStart.value.tx + (e.clientX - flowDragStart.value.x);
      flowTy.value = flowDragStart.value.ty + (e.clientY - flowDragStart.value.y);
    };

    const onFlowMouseUp = () => {
      flowDragging.value = false;
    };

    const flowWrapperRef = ref<HTMLElement | null>(null);

    const flowFitView = () => {
      if (!flowWrapperRef.value || !flowSvgContent.value) return;
      const wrapper = flowWrapperRef.value;
      const svgEl = wrapper.querySelector('svg');
      if (!svgEl) return;
      const svgW = svgEl.getAttribute('width');
      const svgH = svgEl.getAttribute('height');
      if (!svgW || !svgH) return;
      const contentW = parseFloat(svgW);
      const contentH = parseFloat(svgH);
      const wrapperW = wrapper.clientWidth;
      const wrapperH = wrapper.clientHeight;
      const padding = 40;
      const scaleX = (wrapperW - padding) / contentW;
      const scaleY = (wrapperH - padding) / contentH;
      const ns = Math.min(scaleX, scaleY, 1);
      flowScale.value = ns;
      flowTx.value = (wrapperW - contentW * ns) / 2;
      flowTy.value = (wrapperH - contentH * ns) / 2;
    };

    // Dynamic scale style for phone previews so the phone fits entirely in the viewport
    const phoneFrameStyle = computed(() => {
      const availableHeight = viewportHeight.value - 40; // account for viewport padding
      if (availableHeight <= 0) return {};
      const scale = Math.min(1, availableHeight / PHONE_NATIVE_HEIGHT);
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
      };
    });

    // Cache fetched HTML to avoid re-fetching
    const htmlCache = new Map<number, string>();

    const fetchPreview = async (index: number) => {
      // Check cache first
      if (htmlCache.has(index)) {
        currentHtml.value = htmlCache.get(index) || '';
        return;
      }

      loading.value = true;
      error.value = '';
      currentHtml.value = '';

      try {
        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/job/import/html-preview',
          {},
          '',
          { sessionId: props.sessionId, uid: props.uid, index },
        );
        if (response?.html) {
          htmlCache.set(index, response.html);
          currentHtml.value = response.html;
        } else {
          error.value = t('import.htmlPreview.notFound');
        }
      } catch {
        error.value = t('import.htmlPreview.fetchError');
      } finally {
        loading.value = false;
      }
    };

    const goTo = (idx: number) => {
      if (idx < 0 || idx >= totalCount.value) return;
      currentIndex.value = idx;
      resetFlowZoom();
    };

    const getTabLabel = (idx: number): string => {
      if (isDirectMode.value && props.previews[idx]) {
        return props.previews[idx].label;
      }
      return idx === 0
        ? t('import.htmlPreview.primary')
        : `${t('import.htmlPreview.redirection')} ${idx}`;
    };

    // Auto fit-view when flow SVG content appears or changes
    watch(flowSvgContent, (val) => {
      if (val) {
        nextTick(() => flowFitView());
      }
    });

    // Force desktop mode when HTML already contains a phone frame
    watch(hasPhoneWrapper, (val) => {
      if (val) {
        viewMode.value = 'desktop';
      }
    });

    // Fetch HTML when currentIndex changes (fetch mode only)
    watch(currentIndex, (newIdx) => {
      if (isDirectMode.value) {
        currentHtml.value = props.previews[newIdx]?.html || '';
      } else {
        fetchPreview(newIdx);
      }
    });

    onMounted(() => {
      if (viewportRef.value) {
        viewportHeight.value = viewportRef.value.clientHeight;
        resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          entries.forEach((entry: ResizeObserverEntry) => {
            viewportHeight.value = entry.contentRect.height;
          });
        });
        resizeObserver.observe(viewportRef.value);
      }
      if (isDirectMode.value) {
        currentHtml.value = props.previews[0]?.html || '';
      } else {
        fetchPreview(0);
      }
    });

    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });

    return {
      t,
      currentIndex,
      viewMode,
      loading,
      error,
      currentHtml,
      hasPhoneWrapper,
      phoneFrameStyle,
      viewportRef,
      totalCount,
      goTo,
      getTabLabel,
      isFlowPreview,
      flowSvgContent,
      flowCanvasStyle,
      resetFlowZoom,
      flowZoomIn,
      flowZoomOut,
      onFlowWheel,
      onFlowMouseDown,
      onFlowMouseMove,
      onFlowMouseUp,
      flowWrapperRef,
      flowFitView,
    };
  },
});
</script>

<style scoped lang="scss">
.html-preview-slider {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  &__panel {
    display: flex;
    flex-direction: column;
    width: 90vw;
    max-width: 1100px;
    height: 88vh;
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
  }

  // ---- Header ----
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    flex-shrink: 0;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    color: #1e293b;

    i {
      color: #94c748;
    }
  }

  &__count-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    background: #e2e8f0;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: #f1f5f9;
    border-radius: 8px;
    cursor: pointer;
    color: #64748b;
    font-size: 1rem;
    transition: all 0.2s ease;

    &:hover {
      background: #e2e8f0;
      color: #1e293b;
    }
  }

  // ---- Mode toggle (desktop / mobile) ----
  &__mode-toggle {
    display: flex;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }

  // ---- Tabs ----
  &__tabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    overflow-x: auto;
    flex-shrink: 0;

    &::-webkit-scrollbar {
      height: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 2px;
    }
  }

  // ---- Viewport ----
  &__viewport {
    flex: 1;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    background: #f1f5f9;
    padding: 1.25rem;

    &--mobile {
      align-items: center;
    }
  }

  &__frame-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    background: #f9fafb;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    &--mobile {
      width: 430px;
      max-width: 100%;
      height: 100%;
      max-height: 100%;
      background: transparent;
      box-shadow: none;
      border-radius: 0;
      overflow: visible;
    }

    &--phone {
      width: 400px;
      height: 820px;
      background: transparent;
      box-shadow: none;
      border-radius: 0;
      overflow: visible;
      flex-shrink: 0;
    }
  }

  &__flow-canvas {
    display: inline-block;
    user-select: none;

    :deep(svg) {
      display: block;
    }
  }

  &__flow-controls {
    position: absolute;
    bottom: 16px;
    right: 16px;
    display: flex;
    gap: 6px;
    z-index: 5;
  }

  &__iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }

  // ---- Loading / Error states ----
  &__loading,
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    height: 100%;
    color: #64748b;
    font-size: 0.9375rem;

    i {
      font-size: 1.5rem;
    }
  }

  &__error {
    color: #dc2626;

    i {
      color: #f87171;
    }
  }

  // ---- Navigation ----
  &__nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.75rem;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    flex-shrink: 0;
  }
}

// ---- Mode button ----
.mode-btn {
  width: 34px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  font-size: 0.9375rem;
  transition: all 0.2s ease;

  &:hover {
    color: #475569;
    background: #f1f5f9;
  }

  &--active {
    background: #94c748;
    color: #ffffff;

    &:hover {
      background: #7eb33a;
      color: #ffffff;
    }
  }
}

// ---- Slide tab ----
.slide-tab {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    color: #334155;
    background: #f1f5f9;
  }

  &--active {
    color: #94c748;
    border-bottom-color: #94c748;
    background: #ffffff;
  }

  &__icon {
    font-size: 0.6875rem;
  }
}

// ---- Nav arrows ----
.nav-arrow {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  color: #475569;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #94c748;
    border-color: #94c748;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.nav-dots {
  display: flex;
  gap: 0.375rem;
}

.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: #cbd5e1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #94a3b8;
  }

  &--active {
    background: #94c748;
    width: 20px;
    border-radius: 4px;
  }
}

.flow-ctrl-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }

  &--reset {
    width: auto;
    padding: 0 10px;
    font-size: 14px;
  }
}
</style>
